import { isDefined } from "@omni/utils/helper.utils";
import { parseIntSafe } from "@omni/utils/number.utils";
import { cn } from "@omni/utils/styles";
import { ChangeEvent, FocusEvent, useEffect, useMemo, useState } from "react";

import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";
import TooltipSlider from "@/components/General/TooltipSlider/TooltipSlider.component";
import {
    NumericFacetBounds,
    NumericFacetProps
} from "@/components/Search/SearchResults/StateComponents/Facets/Facet/@types/numericFacet.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type Bounds = [lowerBound: number, upperBound: number];

type TemporaryBounds = {
    lowerBound: number;
    upperBound: number;
    lowerBoundInputValue: string;
    upperBoundInputValue: string;
};

const mapToTemporaryBounds = (
    bounds: Required<NumericFacetBounds>
): TemporaryBounds => ({
    ...bounds,
    lowerBoundInputValue: bounds.lowerBound.toString(),
    upperBoundInputValue: bounds.upperBound.toString()
});

const mapSliderValuesToBounds =
    (currentBounds: Required<NumericFacetBounds>) =>
    (values: number[] | number): Bounds =>
        Array.isArray(values)
            ? [
                  values.at(0) ?? currentBounds.lowerBound,
                  values.at(1) ?? currentBounds.upperBound
              ]
            : [values, values];

const NumericFacetComponent = ({
    numericFacetValue,
    currentBounds,
    onChangeBounds
}: NumericFacetProps) => {
    const { t } = useGranite();

    const { lowerBound: min, upperBound: max } = numericFacetValue;

    const bounds = useMemo(
        () => ({
            lowerBound: isDefined(currentBounds?.lowerBound)
                ? Math.max(currentBounds.lowerBound, min)
                : min,
            upperBound: isDefined(currentBounds?.upperBound)
                ? Math.min(currentBounds.upperBound, max)
                : max
        }),
        [currentBounds?.lowerBound, currentBounds?.upperBound, min, max]
    );

    const [temporaryBounds, setTemporaryBounds] = useState<TemporaryBounds>(
        mapToTemporaryBounds(bounds)
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(
        () => setTemporaryBounds(mapToTemporaryBounds(bounds)),
        [bounds, setTemporaryBounds]
    );

    const updateTemporaryBounds = (
        newTemporaryBounds: Partial<TemporaryBounds>
    ) => setTemporaryBounds((state) => ({ ...state, ...newTemporaryBounds }));

    const submitBoundaries = ([lowerBound, upperBound]: Bounds) => {
        const newLowerBound = Math.max(min, lowerBound);
        const newUpperBound = Math.min(max, upperBound);

        updateTemporaryBounds({
            lowerBound: newLowerBound,
            upperBound: newUpperBound,
            lowerBoundInputValue: newLowerBound.toString(),
            upperBoundInputValue: newUpperBound.toString()
        });

        if (
            newLowerBound !== bounds.lowerBound ||
            newUpperBound !== bounds.upperBound
        ) {
            onChangeBounds({
                lowerBound: lowerBound,
                upperBound: upperBound
            });
        }
    };

    const handleOnChangeSlider = (values: number | number[]) => {
        const [newLowerBound, newUpperBound] =
            mapSliderValuesToBounds(bounds)(values);

        updateTemporaryBounds({
            lowerBound: newLowerBound,
            upperBound: newUpperBound,
            lowerBoundInputValue: newLowerBound.toString(),
            upperBoundInputValue: newUpperBound.toString()
        });
    };

    const handleOnChangeCompleteSlider = (values: number | number[]) => {
        const boundaries = mapSliderValuesToBounds(bounds)(values);
        submitBoundaries(boundaries);
    };

    const handleOnInputBlur = () =>
        submitBoundaries([
            temporaryBounds.lowerBound,
            temporaryBounds.upperBound
        ]);

    const handleOnInputFocus = (
        event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        event.target.select();
    };

    const handleOnLowerBoundChange = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = parseIntSafe(event.target.value);

        const newTemporaryBounds: Partial<TemporaryBounds> = {
            lowerBoundInputValue: event.target.value
        };

        if (isDefined(value)) {
            newTemporaryBounds.lowerBound = Math.min(
                temporaryBounds.upperBound,
                Math.max(min, value)
            );
        }

        updateTemporaryBounds(newTemporaryBounds);
    };

    const handleOnUpperBoundChange = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = parseIntSafe(event.target.value);

        const newTemporaryBounds: Partial<TemporaryBounds> = {
            upperBoundInputValue: event.target.value
        };

        if (isDefined(value)) {
            newTemporaryBounds.upperBound = Math.max(
                temporaryBounds.lowerBound,
                Math.min(max, value)
            );
        }

        updateTemporaryBounds(newTemporaryBounds);
    };

    return (
        <>
            <div className={cn("tw:flex tw:items-baseline form-group")}>
                <Label className={cn("tw:pr-2")}>
                    {t("search.price.from")}
                </Label>
                <Input
                    min={min}
                    max={temporaryBounds.upperBound}
                    value={temporaryBounds.lowerBoundInputValue}
                    onChange={handleOnLowerBoundChange}
                    onBlur={handleOnInputBlur}
                    onFocus={handleOnInputFocus}
                    type={"number"}
                    pattern={"[0-9]*"}
                />
                <Label className={cn("tw:px-2")}>{t("search.price.to")}</Label>
                <Input
                    min={temporaryBounds.lowerBound}
                    max={max}
                    value={temporaryBounds.upperBoundInputValue}
                    onChange={handleOnUpperBoundChange}
                    onBlur={handleOnInputBlur}
                    onFocus={handleOnInputFocus}
                    type={"number"}
                    pattern={"[0-9]*"}
                />
            </div>
            <div className={cn("tw:px-4 tw:pb-2")}>
                <TooltipSlider
                    classNames={{
                        handle: cn("tw:select-none")
                    }}
                    range={true}
                    allowCross={false}
                    onChangeComplete={handleOnChangeCompleteSlider}
                    onChange={handleOnChangeSlider}
                    tipVisible={true}
                    min={min}
                    max={max}
                    value={[
                        temporaryBounds.lowerBound,
                        temporaryBounds.upperBound
                    ]}
                />
            </div>
        </>
    );
};

export { NumericFacetComponent };
